import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  uploadTaxEntriesToImport,
  fetchTaxEntryToImportData,
  submitBatchTaxEntriesForApproval,
  resetDataForBatchTaxEntries,
} from './store/actions';
import {
  areImportsPendingSelector,
  invalidTaxEntriesFoundSelector,
  taxEntriesToImportSelector,
} from '../store/selectors';
import {
  environmentSuffixSelector,
} from '../../shared/store/selectors';

import Button from '../../shared/button.component';
import { ReduxAlert } from '../../shared/alert.component';
import Attachments from '../../shared/forms/attachments.component';

import HamburgerDropdown from './hamburgerDropdown.component';
import BatchTaxEntriesGrid from './batchTaxEntriesGrid.component';
import TaxEntryToImportDetails, { proposedJournalsPropTypes } from './taxEntryToImportDetails.component';
import taxEntryToImportPropTypes from './taxEntryToImport.propTypes';
import { YES_FLAG, NO_FLAG } from '../../shared/constants';

import SecondaryMenu from '../../menu/secondaryMenu.component';

import mapFields from '../../utils/fieldMapping';
import NavigationPrompt, { IMPORTED_DATA_PENDING_MESSAGE } from '../../shared/navigationPrompt.component';
import { ClientIsLockedOverlay } from '../../shared/clientLocked/ClientLockedOverlay';

const mappedData = data => ({
  ...mapFields({
    taxYear: 'taxYear',
    countryTaxTypeDescription: 'countryTaxTypeDescription',
    entryType: 'taxEntryTypeDescription',
    entryActivity: 'taxEntryActivityDescription',
    bookType: 'bookTypeDescription',
    account: 'adjustmentDescription',
    amount: 'amount',
    fromAccount: 'fromAdjustmentDescription',
    glAccountOverride: 'glAccountOffsetDescription',
    comment: 'taxEntryComment',
  })(data),
  taxEntryType: data.entryType,
  topsideFlag: data.topsideFlag === true || data.topsideFlag === YES_FLAG ? YES_FLAG : NO_FLAG,
  fbosFlag: data.fbosFlag === true || data.fbosFlag === YES_FLAG ? YES_FLAG : NO_FLAG,
});

class BatchTaxEntriesPage extends PureComponent {
  static propTypes = {
    currentPeriod: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    taxEntriesLoading: PropTypes.bool.isRequired,
    uploadTaxEntriesToImport: PropTypes.func.isRequired,
    fetchTaxEntryToImportData: PropTypes.func.isRequired,
    submitBatchTaxEntriesForApproval: PropTypes.func.isRequired,
    resetDataForBatchTaxEntries: PropTypes.func.isRequired,
    isUploadingTaxEntriesToImport: PropTypes.bool.isRequired,
    taxEntriesToImport: PropTypes.arrayOf(taxEntryToImportPropTypes).isRequired,
    chosenTaxEntry: taxEntryToImportPropTypes,
    proposedJournals: proposedJournalsPropTypes,
    invalidTaxEntriesFound: PropTypes.bool.isRequired,
    areImportsPending: PropTypes.bool.isRequired,
    numberOfErrors: PropTypes.number.isRequired,
    insertedTaxEntries: PropTypes.number.isRequired,
    user: PropTypes.shape({
      sso: PropTypes.string.isRequired,
    }).isRequired,
    environmentSuffix: PropTypes.string.isRequired,
  };

  state = {
    files: [],
    isFormValid: false,
    batchInfo: null,
  };

  componentWillUnmount() {
    this.props.resetDataForBatchTaxEntries();
  }

  uploadTaxEntriesToImport = async file => {
    const { currentPeriod, uploadTaxEntriesToImport } = this.props;
    await uploadTaxEntriesToImport(file, currentPeriod);
    this.validate();
    this.setState({
      batchInfo: this.getBatchInfo(),
    });
  };

  chooseTaxEntry = chosenTaxEntry => {
    const { currentPeriod, fetchTaxEntryToImportData } = this.props;
    if (chosenTaxEntry) {
      fetchTaxEntryToImportData(chosenTaxEntry, currentPeriod);
    }
  };

  addFileToForm = file => {
    this.setState(prevState => ({
      files: [
        ...prevState.files,
        file,
      ],
    }), () => this.validate());
  };

  removeFileFromForm = fileToRemove => {
    this.setState(({ files }) => ({
      files: files.filter(({ fileName }) => fileName !== fileToRemove.fileName),
    }), () => this.validate());
  };

  validate = () => {
    const { files } = this.state;
    const { invalidTaxEntriesFound, taxEntriesToImport } = this.props;

    if (files.length !== 0 && taxEntriesToImport.length !== 0 && !invalidTaxEntriesFound) {
      this.setState({ isFormValid: true });
      return;
    }

    this.setState({ isFormValid: false });
  };

  submitTaxEntries = () => {
    const {
      currentPeriod,
      taxEntriesToImport,
      submitBatchTaxEntriesForApproval,
    } = this.props;
    const { files } = this.state;

    submitBatchTaxEntriesForApproval({
      taxEntriesToImport,
      currentPeriod,
      files,
    }, () => this.setState({
      files: [],
      batchInfo: null,
      isFormValid: false,
    }));
  };

  getBatchInfo = () => {
    const { user } = this.props;
    const currentDate = new Date();
    const dateText = `${currentDate.getDate()}_${currentDate.getMonth() + 1}_${currentDate.getFullYear()}`;
    const timeText = currentDate.toTimeString().substring(0, 8);
    return `${dateText}@${timeText} ${user.sso}`;
  };

  render() {
    const {
      currentPeriod,
      isUploadingTaxEntriesToImport,
      taxEntriesToImport,
      taxEntriesLoading,
      chosenTaxEntry,
      proposedJournals,
      invalidTaxEntriesFound,
      areImportsPending,
      numberOfErrors,
      insertedTaxEntries,
      environmentSuffix,
    } = this.props;

    const { files, isFormValid, batchInfo } = this.state;

    return (
      <div className="batch-tax-entries-page">
        <ClientIsLockedOverlay message="You are not allowed to batch tax entries while the client is locked">
          <NavigationPrompt
            show={areImportsPending}
            message={IMPORTED_DATA_PENDING_MESSAGE}
          />
          <SecondaryMenu>
            <div className="current-period">{currentPeriod.name}</div>

            <div className="hamburger-dropdown ml-auto text-right">
              <HamburgerDropdown
                uploadTaxEntriesToImport={this.uploadTaxEntriesToImport}
                uploadUnavailable={isUploadingTaxEntriesToImport || taxEntriesLoading}
                downloadTemplateUnavailable={isUploadingTaxEntriesToImport || taxEntriesLoading}
                templateFileName={`TLS_Balance_Sheet_Management_Tax_Entry_${currentPeriod.name}${environmentSuffix}.xlsx`}
              />
            </div>
          </SecondaryMenu>
          <div className="page">
            <div className="container-fluid">
              <ReduxAlert/>
              <div className="row no-gutters page-content-container">
                <div className="col-xs-12 col-md-4">
                  <h5>Batch Tax Entries</h5>
                </div>
                <div className="col-xs-12 col-md-4">
                  {taxEntriesToImport.length ? (
                    <div className="text-center">
                      Import complete. {taxEntriesToImport.length} records imported, {numberOfErrors} with errors
                    </div>
                  ) : null}
                  {insertedTaxEntries ? (
                    <div className="text-center">
                      {insertedTaxEntries} Records saved.
                    </div>
                  ) : null}
                </div>
                <div className="col-xs-12 col-md-4">
                  {taxEntriesToImport.length ? (
                    <div className="float-right">
                      Batch: {batchInfo}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col">
                  <BatchTaxEntriesGrid
                    chooseTaxEntry={this.chooseTaxEntry}
                    taxEntriesToImport={taxEntriesToImport}
                    showValidationColumn={invalidTaxEntriesFound}
                    isLoading={isUploadingTaxEntriesToImport || taxEntriesLoading}
                  />
                </div>
              </div>
              <div className="row mb-2">
                <div className="col">
                  <div className="d-flex flex-row align-items-end page-content-container">
                    <Attachments
                      addFileToForm={this.addFileToForm}
                      removeFileFromForm={this.removeFileFromForm}
                      files={files}
                      maxFileSize={10}
                      required
                      disabled={taxEntriesLoading}
                      headerText="Batch Attachments"
                    />
                    <Button
                      type="button"
                      kind="primary"
                      disabled={!isFormValid || taxEntriesLoading}
                      className="ml-auto"
                      onClick={this.submitTaxEntries}
                    >
                      Submit Entry for Review and Approval
                    </Button>
                  </div>
                </div>
              </div>
              <div className="row mb-2">
                <div className="col">
                  {!chosenTaxEntry ? null : (
                    <TaxEntryToImportDetails
                      taxEntry={mappedData(chosenTaxEntry)}
                      proposedJournals={proposedJournals}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </ClientIsLockedOverlay>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { shared, balanceSheetManagement, user } = state;

  return {
    currentPeriod: shared.currentPeriod,
    isUploadingTaxEntriesToImport: balanceSheetManagement.shared.batchTaxEntries.isUploadingTaxEntriesToImport,
    taxEntriesLoading: balanceSheetManagement.shared.batchTaxEntries.taxEntriesLoading,
    taxEntriesToImport: taxEntriesToImportSelector(state),
    chosenTaxEntry: balanceSheetManagement.shared.batchTaxEntries.chosenTaxEntry,
    proposedJournals: balanceSheetManagement.shared.proposedJournals,
    invalidTaxEntriesFound: invalidTaxEntriesFoundSelector(state),
    areImportsPending: areImportsPendingSelector(state),
    numberOfErrors: balanceSheetManagement.shared.batchTaxEntries.taxEntriesToImport.filter(
      taxEntry => taxEntry.validationError
    ).length,
    insertedTaxEntries: balanceSheetManagement.shared.batchTaxEntries.insertedTaxEntries,
    user: user.user,
    environmentSuffix: environmentSuffixSelector(state),
  };
}

export default connect(
  mapStateToProps,
  {
    uploadTaxEntriesToImport,
    fetchTaxEntryToImportData,
    submitBatchTaxEntriesForApproval,
    resetDataForBatchTaxEntries,
  }
)(BatchTaxEntriesPage);
